import React, { useEffect, useRef, useState } from 'react';

import { VideoRoundProgressBar } from './components/RoundProgressBar';
import { saveLocalVideoProgress, getLocalVideoProgress, markVideoCompleted } from '@/utils/localVideoProgress';
import {
    GenericVideoPlayerInterface,
    VideoPlayerCoreProps,
    VideoPlayerType,
    checkMilestones,
    detectSeek,
    trackVideoEngagement,
} from './videoUtils';
import { YouTubePlayerCore } from './YoutubePlayer';
import { CloudflarePlayerCore } from './CloudflarePlayer';
import { TwitchPlayerCore } from './TwitchPlayer';

interface VideoPlayerProps {
    videoUrl: string;
    videoId: string;
    videoStorageKeyOverride?: string; // What we save video progress under in local storage
    playerType: VideoPlayerType;
    className?: string;
    onComplete?: (isFirstWatch: boolean) => void;
    onMilestone?: (progress: number, isFirstWatch: boolean) => void;
    enforcedViewing?: boolean; // Disables video controls, shows progress circle and stores progress until video has been viewed once
    roundProgressBarClassName?: string;
    completionTextClassName?: string;
    hasCompletionText?: boolean;
    preCompletionText?: string;
    postCompletionText?: string;
}

export function VideoPlayer({
    videoUrl,
    videoId,
    videoStorageKeyOverride,
    playerType,
    className,
    onComplete,
    enforcedViewing,
    onMilestone,
    roundProgressBarClassName,
    completionTextClassName,
    hasCompletionText,
    preCompletionText,
    postCompletionText,
}: VideoPlayerProps) {
    const playerRef = useRef<GenericVideoPlayerInterface | null>(null);
    const pollingIntervalRef = useRef<number | null>(null);
    const progressIntervalRef = useRef<number | null>(null);
    const stopPollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastKnownTimeRef = useRef<number>(0);
    const lastKnownDurationRef = useRef<number>(0);
    const milestonesTrackedRef = useRef<Set<string>>(new Set());
    const onCompleteRef = useRef<((isFirstWatch: boolean) => void) | undefined>(onComplete);
    const onVideoCompleteCallbackHasBeenCalledRef = useRef<boolean>(false);
    const onMilestoneRef = useRef<((val: number, isFirstWatch: boolean) => void) | undefined>(onMilestone);

    const videoStorageKey = videoStorageKeyOverride ?? videoId;
    const savedData = useRef(getLocalVideoProgress(videoStorageKey));
    const [hasCompletedAtLeastOnce, setHasCompletedAtLeastOnce] = useState(() => {
        return savedData?.current?.completed === true;
    });

    // Component config
    const hasControls = !enforcedViewing || hasCompletedAtLeastOnce;
    const shouldAutoplay = true; // Previously was !hasControls but we think its useful to always autoplay
    const hasProgressCircle = enforcedViewing;
    const shouldPersistProgress = enforcedViewing && !hasCompletedAtLeastOnce;

    // Keep onComplete & onMilestone refs up to date
    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    useEffect(() => {
        onMilestoneRef.current = onMilestone;
    }, [onMilestone]);

    const onVideoComplete = () => {
        // Stop oncomplete being called multiple times
        if (!onVideoCompleteCallbackHasBeenCalledRef.current) {
            onCompleteRef.current?.(!hasCompletedAtLeastOnce);
            onVideoCompleteCallbackHasBeenCalledRef.current = true;
        }

        console.log('User has finished the video');
        stopPolling();
        stopProgressSaving();
        markVideoCompleted(videoStorageKey);
        setTimeout(() => {
            setHasCompletedAtLeastOnce(true);
            milestonesTrackedRef.current?.clear();
        }, 100);
    };

    const onVideoMilestone = (val: number) => {
        const getIsFirstTimeMilestoneReached = () => {
            if (hasCompletedAtLeastOnce) return false;
            if (!lastKnownDurationRef.current) return true;
            // If user has not completed the video for the first time yet...
            // Check the saved progress to see if we already reached milestone on previous session
            // (Can't just check milestonesTrackedRef as it gets cleared when component remounts)
            const savedProgress = savedData.current?.progress;
            const savedPercent = (savedProgress / lastKnownDurationRef.current) * 100 || 0;
            return val > savedPercent;
        };

        onMilestoneRef.current?.(val, getIsFirstTimeMilestoneReached());
    };

    const startProgressSaving = (player: GenericVideoPlayerInterface) => {
        if (!shouldPersistProgress) return;
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = window.setInterval(() => {
            try {
                const currentTime = player.getCurrentTime();
                if (currentTime) {
                    saveLocalVideoProgress(videoStorageKey, currentTime);
                }
            } catch (error) {
                console.log('Warning: Video progress not saved', error);
            }
        }, 2000);
    };

    const stopProgressSaving = () => {
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
    };

    const startPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        pollingIntervalRef.current = window.setInterval(() => {
            const player = playerRef.current;
            if (!player) return;

            try {
                const currentTime = player.getCurrentTime();
                const duration = player.getDuration();

                if (typeof currentTime !== 'number' || typeof duration !== 'number') return;

                lastKnownTimeRef.current = detectSeek(currentTime, duration, videoUrl, lastKnownTimeRef.current);
                lastKnownDurationRef.current = duration;

                checkMilestones(
                    currentTime,
                    duration,
                    videoUrl,
                    milestonesTrackedRef.current,
                    onVideoComplete,
                    onVideoMilestone,
                );

                // Backup end detection which runs on poll
                if (player.isEnded()) {
                    onVideoComplete();
                }
            } catch (error) {
                console.log('Warning: Polling error', error);
            }
        }, 500);
    };

    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    const handlePlay = () => {
        clearTimeout(stopPollingTimeoutRef.current);
        startPolling();
    };

    const handlePause = () => {
        // Wait 1000ms before stopping to allow end events to send via poll if needed
        stopPollingTimeoutRef.current = setTimeout(stopPolling, 1000);
    };

    const handleEnded = () => {
        stopPolling();
        onVideoComplete();
    };

    const handleReady = () => {
        const player = playerRef.current;
        if (!player) return;

        if (shouldPersistProgress) {
            startProgressSaving(player);
            // No longer seeking video on load due to bugs
            // const savedData = getLocalVideoProgress(videoStorageKey);
            // setTimeout(() => {
            //     seekVideo(savedData?.progress, player);
            // }, 500);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            const currentTime = lastKnownTimeRef.current;
            const duration = lastKnownDurationRef.current;

            if (currentTime > 0 && duration > 0) {
                trackVideoEngagement('closed', videoUrl, [], [currentTime, duration]);
                if (shouldPersistProgress) saveLocalVideoProgress(videoStorageKey, currentTime);
            }

            stopPolling();
            stopProgressSaving();
            lastKnownTimeRef.current = 0;
            lastKnownDurationRef.current = 0;
            milestonesTrackedRef.current.clear();
        };
    }, [videoUrl, videoId, shouldPersistProgress]);

    const coreProps: VideoPlayerCoreProps = {
        videoUrl,
        videoId,
        className,
        hasControls,
        shouldAutoplay,
        playerRef,
        onPlay: handlePlay,
        onPause: handlePause,
        onEnded: handleEnded,
        onReady: handleReady,
    };

    return (
        <>
            {hasCompletionText && preCompletionText && postCompletionText && (
                <div className={completionTextClassName}>
                    <span>{hasCompletedAtLeastOnce ? postCompletionText : preCompletionText}</span>
                </div>
            )}
            {hasProgressCircle && (
                <VideoRoundProgressBar
                    playerRef={playerRef}
                    className={roundProgressBarClassName}
                    force100={hasCompletedAtLeastOnce}
                />
            )}
            {playerType === 'youtube' && <YouTubePlayerCore {...coreProps} />}
            {playerType === 'cloudflare' && <CloudflarePlayerCore {...coreProps} />}
            {playerType === 'twitch' && <TwitchPlayerCore {...coreProps} />}
        </>
    );
}
